import { clsx } from 'clsx';
import {
  type ComponentPropsWithRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Chip } from '@repo/ui/Chip';
import { Flex } from '@repo/ui/Flex';

import s from './Input.module.css';

export interface TextareaProps extends ComponentPropsWithRef<'textarea'> {
  className?: string;
  id: string;
  name: string;
  maxLength?: number;
  requiredText?: string;
  optionalText?: string;
}

export const Textarea = (props: TextareaProps) => {
  const {
    className,
    defaultValue,
    maxLength,
    placeholder,
    onChange,
    ref: externalRef,
    required,
    rows = 5,
    requiredText = 'Required',
    optionalText = 'Optional',
    ...attrs
  } = props;

  const internalRef = useRef<HTMLTextAreaElement>(null);
  const [count, setCount] = useState<number>(0);

  const isCounterEnabled = maxLength && maxLength > 0;

  const mergeRefs = useCallback(
    (element: HTMLTextAreaElement | null) => {
      // Update internal ref
      internalRef.current = element;

      // Update external ref if provided
      if (externalRef) {
        if (typeof externalRef === 'function') {
          externalRef(element);
        } else if (externalRef && typeof externalRef === 'object') {
          externalRef.current = element;
        }
      }
    },
    [externalRef],
  );

  useEffect(() => {
    if (internalRef.current) {
      setCount(internalRef.current.value.length);
    }
  }, [defaultValue]);

  const handleChange: ComponentPropsWithRef<'textarea'>['onChange'] = (e) => {
    if (internalRef.current && isCounterEnabled) {
      setCount(internalRef.current.value.length);
    }

    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className={clsx(s.inputWrap, className)}>
      <textarea
        className={clsx(s.textarea)}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={handleChange}
        ref={mergeRefs}
        rows={rows}
        style={{ ...attrs.style, minHeight: `${rows * 0.9375}rem` }}
        {...attrs}
      ></textarea>
      <Flex alignItems="center" gap="g-8" justifyContent="space-between">
        <div className={s.count}>
          {isCounterEnabled && `${count} / ${maxLength}`}
        </div>
        <Chip isReadOnly className={s.requiredChip}>
          {required ? requiredText : optionalText}
        </Chip>
      </Flex>
    </div>
  );
};
